import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  defaultAnimateLayoutChanges,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import PropTypes from 'prop-types';
import BlockListItem from './BlockListItem';
import BlockListShape from './BlockListShape';

const propTypes = {
  dataSource: PropTypes.arrayOf(BlockListShape),
  onSortEnd: PropTypes.func,
  reOrderBlocks: PropTypes.bool,
  isStateUpdated: PropTypes.bool,
  deleteBlockId: PropTypes.string,
};
const defaultProps = {
  reOrderBlocks: false,
  isStateUpdated: false,
  deleteBlockId: '',
  dataSource: [],
  onSortEnd: () => {},
};

// TODO: Use Presentational Component for rendering (to minimize lag during animation): https://docs.dndkit.com/api-documentation/draggable/drag-overlay#presentational-components
export default function BlockList({
  dataSource,
  onSortEnd,
  reOrderBlocks,
  deleteBlockId,
  isStateUpdated,
}) {
  const [items, setItems] = useState(dataSource);

  useEffect(() => {
    if (isStateUpdated) {
      setItems(dataSource);
    }
  }, [isStateUpdated]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((newItems) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newList = arrayMove(newItems, oldIndex, newIndex);
        onSortEnd(newList);
        return newList;
      });
    }
  };

  const animateLayoutChanges = (args) =>
    args.isSorting || args.wasSorting
      ? defaultAnimateLayoutChanges(args)
      : true;

  return (
    <div className="content-blocks">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        animateLayoutChanges={animateLayoutChanges}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
      >
        <SortableContext
          items={items}
          strategy={verticalListSortingStrategy}
          handle
          removable
          useDragOverlay={false}
        >
          {items.map((value, index) => (
            <BlockListItem
              key={`content-block-${value.id}`}
              index={index}
              value={value}
              reOrderBlocks={reOrderBlocks}
              deleteBlockId={deleteBlockId}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

BlockList.propTypes = propTypes;
BlockList.defaultProps = defaultProps;
